// ABOUTME: Static mock context for the TechLog → Skylegs flight transformation, built from captured staging records.
import { TechlogFlightStatus } from '../../../../types/techLog/sector';
import type { TechlogToSkylegsFlightInput } from './validation';

/**
 * TechLog side: the completed CYVR→CYYZ sector on Japat AG (Staging) and its
 * `HB-TEST` Falcon 7X, including the post-flight custom fields Japat actually
 * populate (`max_alt`, `post_rem`, `oil_eng1..3`) while the standard
 * `maxFlightLevel` stays null.
 *
 * Skylegs side: the category list captured from `GET /categories` on the staging
 * tenant "Eagle", keeping the `kind: "Aircraft"` `Private-91` rows that a
 * name-only lookup would wrongly resolve to, and the organisation memberships
 * captured from `GET /organisations/active/users`. The two staging tenants share
 * no crew yet, so the TechLog pilot records carry the captured Skylegs
 * membership emails to exercise the crew match. The destination flight carries a
 * third crew member behind the two flight-deck seats, which is the case the push
 * must not overwrite.
 */
export const mockData: TechlogToSkylegsFlightInput = {
  techlog: {
    sector: {
      id: '8c8fb263-0664-4596-b983-3221df47b7a6',
      tripId: 'f0c8b6a4-4e29-4a0c-8f70-6f4d2f9c1b55',
      departureAirportId: 1394,
      arrivalAirportId: 1421,
      actualArrivalAirportId: 1421,
      pilotInCommandId: 'a1d5e0b7-6c3f-4b2e-9a52-1f0c7d4e8b31',
      secondInCommandId: 'c3f9a2d4-8e17-4c60-b5a8-2d6e9f01a743',
      apuSeconds: 3660,
      apuReading: 3660,
      apuCyclesReading: 0,
      hobbsDeparture: 102.33,
      hobbsArrival: 103.67,
      offblocksTime: '2026-07-16T14:05:00.000Z',
      takeoffTime: '2026-07-16T14:20:00.000Z',
      landingTime: '2026-07-16T18:35:00.000Z',
      onblocksTime: '2026-07-16T18:48:00.000Z',
      landingsCount: 1,
      takeoffsCount: 1,
      status: TechlogFlightStatus.COMPLETE,
      remarks: null,
      createdAt: '2026-07-16T12:00:00.000Z',
      updatedAt: '2026-07-16T19:02:00.000Z',
      date: '2026-07-16',
      source: 'techlogApp',
      preflightCustomData: {},
      postflightCustomData: {
        max_alt: 56,
        post_rem: 'Passengers were happy',
        oil_eng1: 55,
        oil_eng2: 54,
        oil_eng3: 57,
      },
      cycles: {
        airframe: 1,
        engine_1: 1,
        engine_2: 1,
        engine_3: 1,
        apu: 0,
      },
      passengersMale: 2,
      maxFlightLevel: null,
      actualFuelBurn: 1001,
    },
    trip: {
      id: 'f0c8b6a4-4e29-4a0c-8f70-6f4d2f9c1b55',
      aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
      pilotInCommandId: 'a1d5e0b7-6c3f-4b2e-9a52-1f0c7d4e8b31',
      secondInCommandId: 'c3f9a2d4-8e17-4c60-b5a8-2d6e9f01a743',
      date: '2026-07-16',
      number: 214,
      srpNumber: 3,
      callsign: 'HBTEST',
      isComplete: true,
      createdAt: '2026-07-16T11:50:00.000Z',
      updatedAt: '2026-07-16T19:02:00.000Z',
    },
    aircraft: {
      id: '88aae428-6936-4bea-b756-3c1c3b76d405',
      operatorId: '221173da-5926-47b3-8205-5073730ae1f5',
      aircraftTypeId: 'ad10c2d6-208a-413c-8f9b-9d6e1332b40f',
      serialNumber: '543-089',
      registration: 'HB-TEST',
      year: 2018,
      fuelUnit: 'lb',
      oilUnit: 'qt',
      maxFuelValue: 32000,
      monthlyCycles: 2,
      monthlyFlightSeconds: 21360,
      monthlyApuSeconds: 3660,
      maintenanceType: 'hours',
      apuMode: 'totalled',
      flightStatus: 'onGround',
      createdAt: '2026-07-17T00:00:00.000Z',
      updatedAt: '2026-07-16T19:02:00.000Z',
      aircraftType: {
        id: 'ad10c2d6-208a-413c-8f9b-9d6e1332b40f',
        engineCount: '3',
        engineType: 'jet',
        weightCategory: 'heavy',
      },
    },
    fuelSummary: {
      preUpliftFuel: 2501,
      postUpliftFuel: 3664,
      usedFuel: 2164,
      remainingFuel: 1500,
    },
    fuelUplifts: [
      {
        id: '4c1d9a70-52b8-4f3d-9c1e-7b0a6d2e8f41',
        associatedRecordId: '8c8fb263-0664-4596-b983-3221df47b7a6',
        associatedRecordType: 'Flight',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        aircraftValuePreUplift: 2501,
        aircraftValuePostUplift: 3664,
        aircraftValueActualUplift: 1133.7,
        density: 0.8,
        fuelType: 'JET-A1',
        safPercentage: 0.1,
        paid: true,
        aircraftUnit: 'lb',
        createdAt: '2026-07-16T13:40:00.000Z',
        updatedAt: '2026-07-16T13:40:00.000Z',
      },
    ],
    fluidUplifts: [
      {
        id: '9a2f6c31-7d84-4b05-8e19-3c6a0f5d2b78',
        category: null,
        adjustment: null,
        uplift: 0.5,
        associatedRecordId: '8c8fb263-0664-4596-b983-3221df47b7a6',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'engine',
        fluidDestinationNumber: 1,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 5,
        departureValue: 5.5,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:45:00.000Z',
        updatedAt: '2026-07-16T13:45:00.000Z',
      },
      {
        id: 'd47b1e08-3a95-4c62-b0f7-8e5d1a9c2064',
        category: null,
        adjustment: null,
        uplift: 0.25,
        associatedRecordId: '8c8fb263-0664-4596-b983-3221df47b7a6',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'apu',
        fluidDestinationNumber: null,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 2,
        departureValue: 2.25,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:46:00.000Z',
        updatedAt: '2026-07-16T13:46:00.000Z',
      },
    ],
    deiceEvents: [],
    pilotInCommand: {
      id: 'a1d5e0b7-6c3f-4b2e-9a52-1f0c7d4e8b31',
      operatorId: '221173da-5926-47b3-8205-5073730ae1f5',
      firstName: 'Bob',
      lastName: 'Bobson',
      email: 'bob@skylegs.com',
      position: 'Pilot',
      employeeNumber: null,
      amosUserSign: null,
      status: 'active',
      hasLogin: true,
      createdAt: '2026-07-17T00:00:00.000Z',
      updatedAt: '2026-07-17T00:00:00.000Z',
    },
    secondInCommand: {
      id: 'c3f9a2d4-8e17-4c60-b5a8-2d6e9f01a743',
      operatorId: '221173da-5926-47b3-8205-5073730ae1f5',
      firstName: 'Maxim',
      lastName: 'Schelfhout',
      email: 'maxim+1@skylegs.com',
      position: 'Pilot',
      employeeNumber: null,
      amosUserSign: null,
      status: 'active',
      hasLogin: true,
      createdAt: '2026-07-17T00:00:00.000Z',
      updatedAt: '2026-07-17T00:00:00.000Z',
    },
  },
  skylegs: {
    categories: [
      {
        id: 18,
        name: 'Commercial',
        kind: 'MissionFlight',
        utilization: 'commercial',
      },
      {
        id: 19,
        name: 'Private',
        kind: 'MissionFlight',
        utilization: 'private',
      },
      { id: 171, name: 'Training', kind: 'MissionFlight', utilization: null },
      {
        id: 2145,
        name: 'Repositioning',
        kind: 'MissionFlight',
        utilization: null,
      },
      {
        id: 2175,
        name: 'Maintenance',
        kind: 'MissionFlight',
        utilization: null,
      },
      { id: 104, name: 'Private-91', kind: 'Aircraft', utilization: null },
      { id: 2178, name: 'Private-91k', kind: 'Aircraft', utilization: null },
      { id: 3676, name: 'Bird strike', kind: 'Finding', utilization: null },
    ],
    organisationUsers: [
      {
        id: 6304,
        first_name: 'Bob',
        middle_name: null,
        last_name: 'Bobson',
        status: 'active',
        pivot: {
          code: 'BOB',
          status: 'active',
          email: 'bob@skylegs.com',
          homebase_id: 3624,
        },
      },
      {
        id: 2114,
        first_name: 'Maxim',
        middle_name: null,
        last_name: 'Schelfhout',
        status: 'active',
        pivot: {
          code: 'MSC',
          status: 'active',
          email: 'maxim+1@skylegs.com',
          homebase_id: 3624,
        },
      },
      {
        id: 9257,
        first_name: 'Ada',
        middle_name: null,
        last_name: 'Adams',
        status: 'active',
        pivot: {
          code: 'AME',
          status: 'active',
          email: 'ada@skylegs.com',
          homebase_id: 3624,
        },
      },
    ],
    flight: {
      id: 197094,
      mission_id: 83487,
      category_id: 19,
      aircraft_id: 11732,
      departure_id: 3576,
      arrival_id: 3797,
      actual_arrival_id: null,
      alternate_id: null,
      alternate2_id: null,
      number: null,
      flight_number: null,
      mission_flight_identifier: null,
      status: 'COMPLETED',
      obt: '2026-07-16T09:05:00-05:00',
      tot: '2026-07-16T09:20:00-05:00',
      ldt: '2026-07-16T13:35:00-05:00',
      ibt: '2026-07-16T13:48:00-05:00',
      delay: null,
      actual_est: null,
      actual_obt: null,
      actual_tot: null,
      actual_ldt: null,
      actual_ibt: null,
      actual_eqt: null,
      pax: 2,
      actual_pax: null,
      // Two flight-deck seats and a cabin crew member, the case the outbound
      // push has to leave standing.
      crew_string: 'BOB MSC AME',
    },
  },
};

/**
 * A sector recording every post-flight fact this path can read: all four
 * post-flight custom fields including a fourth engine, airframe and APU cycles,
 * every passenger category, an APU meter reading with the sector's APU time and
 * its cycles counter, hobbs readings, a trip number, a fuel summary with two
 * uplift records carrying density and SAF, per-engine oil levels and uplifts on
 * a four-engine aircraft, and de-ice events of a single fluid type.
 *
 * Held apart from `mockData` — which stays the captured staging sector, sparse
 * where Japat's data is sparse — so that a payload assertion over this one names
 * every field the path emits rather than only the fields that sector happened to
 * fill.
 *
 * The Skylegs side is the same tenant lookups: the categories the flight
 * category resolves against, the memberships carrying crew seat codes, and the
 * destination flight whose third crew member the push must leave standing.
 */
export const fullyPopulatedMockData: TechlogToSkylegsFlightInput = {
  techlog: {
    sector: {
      id: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
      tripId: 'f0c8b6a4-4e29-4a0c-8f70-6f4d2f9c1b55',
      departureAirportId: 1394,
      arrivalAirportId: 1421,
      actualArrivalAirportId: 1421,
      pilotInCommandId: 'a1d5e0b7-6c3f-4b2e-9a52-1f0c7d4e8b31',
      secondInCommandId: 'c3f9a2d4-8e17-4c60-b5a8-2d6e9f01a743',
      // Both in seconds: the meter stands at 2609:00 and the APU ran 1:30.
      apuSeconds: 5400,
      apuReading: 9392400,
      apuCyclesReading: 418,
      hobbsDeparture: 102.33,
      hobbsArrival: 103.67,
      offblocksTime: '2026-07-16T14:05:00.000Z',
      takeoffTime: '2026-07-16T14:20:00.000Z',
      landingTime: '2026-07-16T18:35:00.000Z',
      onblocksTime: '2026-07-16T18:48:00.000Z',
      landingsCount: 2,
      takeoffsCount: 2,
      status: TechlogFlightStatus.COMPLETE,
      // The standard fields are populated alongside the custom fields they lose
      // to, so the precedence is visible in the emitted payload.
      remarks: 'Standard field remarks',
      createdAt: '2026-07-16T12:00:00.000Z',
      updatedAt: '2026-07-16T19:02:00.000Z',
      date: '2026-07-16',
      source: 'techlogApp',
      preflightCustomData: {},
      postflightCustomData: {
        max_alt: 410,
        post_rem: 'Passengers were happy',
        oil_eng1: 55,
        oil_eng2: 54,
        oil_eng3: 57,
        oil_eng4: 56,
      },
      cycles: {
        airframe: 2,
        engine_1: 2,
        engine_2: 2,
        engine_3: 2,
        engine_4: 2,
        apu: 1,
        pressure: 2,
      },
      passengersMale: 2,
      passengersFemale: 3,
      passengersChildren: 1,
      passengersInfants: 1,
      maxFlightLevel: 390,
      actualFuelBurn: 2651,
    },
    trip: {
      id: 'f0c8b6a4-4e29-4a0c-8f70-6f4d2f9c1b55',
      aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
      pilotInCommandId: 'a1d5e0b7-6c3f-4b2e-9a52-1f0c7d4e8b31',
      secondInCommandId: 'c3f9a2d4-8e17-4c60-b5a8-2d6e9f01a743',
      date: '2026-07-16',
      number: 214,
      srpNumber: 3,
      callsign: 'HBTEST',
      isComplete: true,
      createdAt: '2026-07-16T11:50:00.000Z',
      updatedAt: '2026-07-16T19:02:00.000Z',
    },
    aircraft: {
      ...mockData.techlog.aircraft,
      aircraftType: {
        id: 'ad10c2d6-208a-413c-8f9b-9d6e1332b40f',
        engineCount: '4',
        engineType: 'jet',
        weightCategory: 'heavy',
      },
    },
    fuelSummary: {
      preUpliftFuel: 2501,
      postUpliftFuel: 4151,
      usedFuel: 2651,
      remainingFuel: 1500,
    },
    // Two records at different densities and SAF blends, so the aggregates are
    // weighted rather than passed through: 800 lb at 0.80 and 850 lb at 0.85 is
    // 1650 lb over 2000 litres.
    fuelUplifts: [
      {
        id: '4c1d9a70-52b8-4f3d-9c1e-7b0a6d2e8f41',
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        aircraftValuePreUplift: 2501,
        aircraftValuePostUplift: 3301,
        aircraftValueActualUplift: 800,
        density: 0.8,
        fuelType: 'JET-A1',
        safPercentage: 0.1,
        paid: true,
        aircraftUnit: 'lb',
        createdAt: '2026-07-16T13:40:00.000Z',
        updatedAt: '2026-07-16T13:40:00.000Z',
      },
      {
        id: 'b8e3f5a2-1c94-4d70-8f61-2a7c0d9e5b34',
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        aircraftValuePreUplift: 3301,
        aircraftValuePostUplift: 4151,
        aircraftValueActualUplift: 850,
        density: 0.85,
        fuelType: 'JET-A1',
        safPercentage: 0.3,
        paid: true,
        aircraftUnit: 'lb',
        createdAt: '2026-07-16T13:42:00.000Z',
        updatedAt: '2026-07-16T13:42:00.000Z',
      },
    ],
    // An oil level and an uplift for each of the four engines and the APU.
    fluidUplifts: [
      {
        id: '9a2f6c31-7d84-4b05-8e19-3c6a0f5d2b71',
        category: null,
        adjustment: null,
        uplift: 0.25,
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'engine',
        fluidDestinationNumber: 1,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 5,
        departureValue: 5.25,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:45:00.000Z',
        updatedAt: '2026-07-16T13:45:00.000Z',
      },
      {
        id: '9a2f6c31-7d84-4b05-8e19-3c6a0f5d2b72',
        category: null,
        adjustment: null,
        uplift: 0.5,
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'engine',
        fluidDestinationNumber: 2,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 5,
        departureValue: 5.5,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:45:00.000Z',
        updatedAt: '2026-07-16T13:45:00.000Z',
      },
      {
        id: '9a2f6c31-7d84-4b05-8e19-3c6a0f5d2b73',
        category: null,
        adjustment: null,
        uplift: 0.75,
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'engine',
        fluidDestinationNumber: 3,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 5,
        departureValue: 5.75,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:45:00.000Z',
        updatedAt: '2026-07-16T13:45:00.000Z',
      },
      {
        id: '9a2f6c31-7d84-4b05-8e19-3c6a0f5d2b74',
        category: null,
        adjustment: null,
        uplift: 1,
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'engine',
        fluidDestinationNumber: 4,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 5,
        departureValue: 6,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:45:00.000Z',
        updatedAt: '2026-07-16T13:45:00.000Z',
      },
      {
        id: 'd47b1e08-3a95-4c62-b0f7-8e5d1a9c2064',
        category: null,
        adjustment: null,
        uplift: 0.25,
        associatedRecordId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        associatedRecordType: 'Flight',
        fluidType: 'oil',
        fluidDestination: 'apu',
        fluidDestinationNumber: null,
        upliftUnit: 'qt',
        aircraftId: '88aae428-6936-4bea-b756-3c1c3b76d405',
        initialValue: 2,
        departureValue: 2.25,
        hydraulicCircuitId: null,
        attachments: [],
        createdAt: '2026-07-16T13:46:00.000Z',
        updatedAt: '2026-07-16T13:46:00.000Z',
      },
    ],
    deiceEvents: [
      {
        id: '6f2c8b41-0d95-4e37-a1b8-5c9d3e07f284',
        sectorId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        startTime: '2026/07/16 01:45 PM',
        endTime: '2026/07/16 01:55 PM',
        fluidType: 'II',
        quantity: 120,
        fluidRatioLeft: 30,
        fluidRatioRight: 70,
      },
      {
        id: '3a9e5d70-8c14-4b62-9f05-7e2b1a6c8d93',
        sectorId: 'e5b0a911-2c47-4f8d-9a63-7d1e4b06c2f9',
        startTime: '2026/07/16 02:00 PM',
        endTime: '2026/07/16 02:08 PM',
        fluidType: 'II',
        quantity: 80,
        fluidRatioLeft: 25,
        fluidRatioRight: 75,
      },
    ],
    pilotInCommand: mockData.techlog.pilotInCommand,
    secondInCommand: mockData.techlog.secondInCommand,
  },
  skylegs: mockData.skylegs,
};
